// src/pages/DriveMoreSales.js
import React from 'react';
import FeaturePageLayout from '../components/FeaturePageLayout';

// Import Icons and Images
import { 
    IoTrendingUpOutline,
    IoCartOutline,
    IoRocketOutline,
    IoChatboxEllipsesOutline,
    IoAnalyticsOutline
} from 'react-icons/io5';
import image1 from '../assets/images/Generate.png';
import image2 from '../assets/images/ConnectSocial.png';
import image3 from '../assets/images/Messages.png';
import image4 from '../assets/images/EngagementRate.png';
import heroBg from '../assets/images/drive-sales-background.png'; 

// --- Structured Data Schemas ---
const breadcrumbSchema = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": [{ "@type": "ListItem", "position": 1, "name": "Home", "item": "https://oolook.in/" }, { "@type": "ListItem", "position": 2, "name": "Drive More Sales", "item": "https://oolook.in/drive-more-sales" }]
};

const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": "Social Commerce",
    "provider": { "@type": "Organization", "name": "Oolook" },
    "name": "AI-Powered Social Selling & Commerce Platform",
    "description": "Turn your social media channels into powerful sales engines. Oolook helps you create high-converting content, engage potential buyers, and track the ROI of every campaign.",
    "url": "https://oolook.in/drive-more-sales"
};

// --- Page-Specific Data ---
const driveMoreSalesPageData = {
  seo: {
    title: "Drive More Sales with Social Media | Oolook AI Commerce",
    description: "Convert followers into customers. Oolook's AI-powered tools help you generate high-converting campaigns, automate lead engagement, and prove your social media ROI.",
    image: 'https://oolook.in/og/drive-sales.png',
    schemas: [breadcrumbSchema, serviceSchema]
  },
  hero: {
    icon: IoTrendingUpOutline,
    title: "Turn Followers into Revenue",
    subtitle: "Connect your social media activity directly to your bottom line. Oolook provides the tools to create high-converting content, engage leads, and track your sales impact.",
    backgroundImage: heroBg
  },
  featureSection: {
    title: "A Sales-Focused Social Media Workflow",
    subtitle: "Every feature is designed to move your audience from discovery to purchase, seamlessly.",
    features: [
      { title: "AI Content that Converts", description: "Generate compelling product posts, promotional copy, and calls-to-action designed to drive clicks and sales.", image: image1 },
      { title: "One-Click Campaign Publishing", description: "Schedule and publish your sales campaigns across all your social channels from a single, unified calendar.", image: image2 },
      { title: "Engage Potential Buyers", description: "Use our Unified Inbox to answer product questions and nurture leads, turning inquiries into conversions.", image: image3 },
      { title: "Track Your Social ROI", description: "Our analytics connect the dots between your posts and your sales, showing you exactly which content drives revenue.", image: image4 }
    ]
  },
  benefitGrid: {
    title: "The Business Impact of Social Selling",
    benefits: [
      { icon: IoCartOutline, title: "Increase Conversions", description: "Create targeted, persuasive content that guides followers down the sales funnel." },
      { icon: IoRocketOutline, title: "Shorten the Sales Cycle", description: "Engage with leads in real-time on the platforms where they are most active." },
      { icon: IoChatboxEllipsesOutline, title: "Build Customer Trust", description: "Provide fast, helpful responses to product questions to build the confidence needed to buy." },
      { icon: IoAnalyticsOutline, title: "Optimize Your Ad Spend", description: "Understand which campaigns deliver the best return on investment and allocate your budget effectively." }
    ]
  },
  stepByStep: {
    title: "Activate Your Social Sales Engine",
    subtitle: "From campaign creation to ROI analysis, Oolook makes social selling simple.",
    steps: [
      { title: "1. Create a Sales-Focused Prompt", description: "Tell the Oolook AI about your product and promotion. For example, 'Generate a campaign for our 20% off summer sale'." },
      { title: "2. Schedule Your Campaign", description: "Drag the AI-generated posts onto your calendar to schedule them across all your relevant social channels." },
      { title: "3. Monitor Engagement & Leads", description: "As potential customers interact with your posts, manage all conversations and inquiries from the Unified Inbox." },
      { title: "4. Analyze Your Sales Impact", description: "Watch your analytics dashboard to see which posts are driving the most traffic, leads, and revenue." }
    ]
  },
  targetAudience: {
    title: "The Ultimate Toolkit for E-commerce & D2C Brands"
  }
};

const DriveMoreSales = () => {
  return (
    <FeaturePageLayout pageData={driveMoreSalesPageData} />
  );
};

export default DriveMoreSales;